import { useEffect, useState } from "react";
import { UploadCloud, FileDown, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { useFideicomiso } from "../lib/fideicomiso";

const REQUIRED = new Set(["credito_id", "principal", "saldo"]);

export default function TapeUpload() {
    const { selected } = useFideicomiso();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({});
    const [fechaCorte, setFechaCorte] = useState("");
    const [busy, setBusy] = useState(false);
    const [commit, setCommit] = useState(null);
    const [cortes, setCortes] = useState([]);

    const loadCortes = async () => {
        const { data } = await api.get("/tape/cortes");
        setCortes(data);
    };
    useEffect(() => {
        loadCortes();
    }, []);

    const onFile = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        setPreview(null);
        setMapping({});
        setCommit(null);
    };

    const onPreview = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/tape/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            // auto-mapping por match de nombre
            const auto = {};
            for (const field of Object.keys(data.system_fields)) {
                const hit = data.columns.find(
                    (c) => c.toLowerCase().replace(/[_\s]/g, "") === field.replace(/_/g, ""),
                );
                if (hit) auto[field] = hit;
            }
            setMapping(auto);
            toast.success(
                `Archivo previsualizado: ${data.total_rows} filas · ${data.columns.length} columnas`,
            );
        } catch (err) {
            toast.error("No se pudo procesar", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setBusy(false);
        }
    };

    const onCommit = async () => {
        if (!preview) return;
        const missing = ["credito_id", "principal", "saldo"].filter((k) => !mapping[k]);
        if (missing.length) {
            toast.error(`Falta mapear: ${missing.join(", ")}`);
            return;
        }
        if (!fechaCorte) {
            toast.error("Selecciona fecha de corte");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/tape/commit", {
                file_id: preview.file_id,
                filename: preview.filename,
                fecha_corte: fechaCorte,
                mapping,
                fideicomiso_id: selected?.id || null,
            });
            setCommit(data);
            toast.success(
                `Corte guardado: ${data.registros_validos}/${data.registros_totales} válidos`,
            );
            loadCortes();
        } catch (err) {
            toast.error("Commit falló", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setBusy(false);
        }
    };

    const downloadRejects = async (corteId) => {
        try {
            const res = await api.get(`/tape/cortes/${corteId}/rejects`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `rechazos_${corteId}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error("No se pudo descargar", {
                description: err?.response?.data?.detail || "Error",
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-8 max-w-[1400px]" data-testid="tape-upload-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                    V1 · Garantía / Fondeo
                </div>
                <h1 className="font-display text-4xl tracking-tight">Ingesta de tape</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Sube un CSV o Excel con la cartera del corte. El sistema detecta columnas,
                    permite mapearlas a campos canónicos, valida tipos y genera reporte de rechazos.
                </p>
            </div>

            {/* Step 1: file */}
            <div className="kpi-card">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Paso 1
                        </div>
                        <h2 className="font-display text-2xl">Cargar archivo</h2>
                    </div>
                    {selected && (
                        <div className="text-xs text-muted-foreground">
                            Fideicomiso destino:{" "}
                            <span className="badge-gold">{selected.numero_interno}</span>
                        </div>
                    )}
                </div>
                <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            CSV o Excel
                        </label>
                        <Input
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={onFile}
                            className="mt-1 file:mr-3 file:rounded-sm file:border-0 file:bg-[color:var(--brand-charcoal)] file:text-[color:var(--brand-paper)] file:px-3 file:py-1.5 file:text-xs file:uppercase file:tracking-widest"
                            data-testid="tape-file-input"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Fecha de corte
                        </label>
                        <Input
                            type="date"
                            value={fechaCorte}
                            onChange={(e) => setFechaCorte(e.target.value)}
                            className="mt-1"
                            data-testid="tape-fecha-corte"
                        />
                    </div>
                    <Button
                        onClick={onPreview}
                        disabled={!file || busy}
                        data-testid="tape-preview-button"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                <UploadCloud className="h-4 w-4 mr-2" /> Previsualizar
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Step 2: mapping + preview */}
            {preview && (
                <div className="kpi-card">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Paso 2
                            </div>
                            <h2 className="font-display text-2xl">Mapear columnas</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Total: <b>{preview.total_rows}</b> filas · Columnas detectadas:{" "}
                                {preview.columns.length}
                            </p>
                        </div>
                        <Button
                            onClick={onCommit}
                            disabled={busy}
                            variant="default"
                            data-testid="tape-commit-button"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Confirmar y guardar corte"
                            )}
                        </Button>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                        {Object.entries(preview.system_fields).map(([field, meta]) => (
                            <div key={field} className="border border-border rounded-sm p-3">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium">{field}</span>
                                    {REQUIRED.has(field) ? (
                                        <span className="badge-mock">REQ</span>
                                    ) : (
                                        <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
                                            opcional
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                                    Tipo · {meta.type}
                                </div>
                                <Select
                                    value={mapping[field] || "__none"}
                                    onValueChange={(v) =>
                                        setMapping({
                                            ...mapping,
                                            [field]: v === "__none" ? undefined : v,
                                        })
                                    }
                                >
                                    <SelectTrigger data-testid={`map-${field}`}>
                                        <SelectValue placeholder="— sin mapear —" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none">— sin mapear —</SelectItem>
                                        {preview.columns.map((c) => (
                                            <SelectItem key={c} value={c}>
                                                {c}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6">
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                            Muestra (primeras 20 filas)
                        </div>
                        <div className="rounded-md border border-border overflow-hidden">
                            <div className="overflow-x-auto max-h-[380px]">
                                <table className="trf-table">
                                    <thead>
                                        <tr>
                                            {preview.columns.map((c) => (
                                                <th key={c}>{c}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.sample.map((row, i) => (
                                            <tr key={i}>
                                                {preview.columns.map((c) => (
                                                    <td key={c} className="text-xs">
                                                        {String(row[c] ?? "")}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3: result */}
            {commit && (
                <div className="kpi-card" data-testid="tape-commit-result">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        Paso 3
                    </div>
                    <h2 className="font-display text-2xl">Resultado de la ingesta</h2>
                    <div className="mt-4 grid md:grid-cols-4 gap-3">
                        <Stat label="Total filas" value={commit.registros_totales} />
                        <Stat
                            label="Válidas"
                            value={commit.registros_validos}
                            tone="success"
                        />
                        <Stat
                            label="Rechazadas"
                            value={commit.registros_rechazados}
                            tone={commit.registros_rechazados ? "danger" : "muted"}
                        />
                        <Stat label="Hash archivo" value={commit.file_hash.slice(0, 12) + "…"} mono />
                    </div>
                    {commit.registros_rechazados > 0 && (
                        <Button
                            onClick={() => downloadRejects(commit.corte_id)}
                            variant="outline"
                            className="mt-4"
                            data-testid="download-rejects"
                        >
                            <FileDown className="h-4 w-4 mr-2" /> Descargar CSV de rechazos
                        </Button>
                    )}
                </div>
            )}

            {/* Cortes guardados */}
            <div className="kpi-card">
                <h2 className="font-display text-2xl mb-3">Cortes guardados</h2>
                <div className="rounded-md border border-border overflow-hidden">
                    <table className="trf-table" data-testid="cortes-table">
                        <thead>
                            <tr>
                                <th>Corte</th>
                                <th>Fecha</th>
                                <th>Archivo</th>
                                <th>Total</th>
                                <th>Válidas</th>
                                <th>Rechazadas</th>
                                <th>Por</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cortes.map((c) => (
                                <tr key={c.corte_id}>
                                    <td className="hash-cell">{c.corte_id.slice(0, 10)}…</td>
                                    <td>{c.fecha_corte}</td>
                                    <td className="text-xs">{c.filename_original}</td>
                                    <td className="tabular">{c.registros_totales}</td>
                                    <td className="tabular text-[color:var(--brand-exito)] font-medium">
                                        {c.registros_validos}
                                    </td>
                                    <td className="tabular text-[color:var(--brand-coral)]">
                                        {c.registros_rechazados}
                                    </td>
                                    <td className="text-xs">{c.uploaded_by}</td>
                                    <td>
                                        {c.registros_rechazados > 0 && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => downloadRejects(c.corte_id)}
                                            >
                                                <FileDown className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {cortes.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                        Aún no hay cortes guardados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, tone = "default", mono = false }) {
    const color =
        tone === "success"
            ? "text-[color:var(--brand-exito)]"
            : tone === "danger"
              ? "text-[color:var(--brand-coral)]"
              : tone === "muted"
                ? "text-muted-foreground"
                : "text-foreground";
    return (
        <div className="border border-border rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {label}
            </div>
            <div
                className={`mt-1 tabular ${mono ? "font-mono text-sm" : "kpi-value text-2xl"} ${color}`}
            >
                {value}
            </div>
        </div>
    );
}
